import {inject} from '@angular/core'
import {HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest} from '@angular/common/http'
import {catchError, switchMap} from 'rxjs/operators'
import {Observable, throwError, from} from 'rxjs'
import {environment} from 'src/environments/environment'
import {OAuthService} from 'angular-oauth2-oidc'
import {ToastrService} from 'ngx-toastr'
import {Router} from '@angular/router'

export const httpRequestInterceptor = (req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
  const oAuthService = inject(OAuthService);
  const router = inject(Router);
  const toastr = inject(ToastrService);

  const lambdaUploadPath = "/media/signed-url/generate/upload";
  const needsAuth = !!(req.url.match(environment.baseUrl) || req.url.includes(lambdaUploadPath));

  if (!needsAuth) {
    return next(req).pipe(
      catchError((error: HttpErrorResponse) => handleError(error, req, router, toastr))
    );
  }

  // Handle token refreshing as an observable
  const refreshToken$ = new Observable<void>((subscriber) => {
    const now = +new Date();
    const expiration = oAuthService.getAccessTokenExpiration();
    const refreshToken = oAuthService.getRefreshToken();

    if (now >= expiration && refreshToken) {
      oAuthService.refreshToken().then(
        () => {
          subscriber.next();
          subscriber.complete();
        },
        (err) => {
          subscriber.error(err);
        }
      );
    } else {
      subscriber.next();
      subscriber.complete();
    }
  });

  return refreshToken$.pipe(
    switchMap(() => {
      let clonedReq = req;
      if (oAuthService.hasValidIdToken()) {
        clonedReq = req.clone({
          setHeaders: {Authorization: `Bearer ${oAuthService.getIdToken()}`},
        });
      }
      return next(clonedReq);
    }),
    catchError((error: HttpErrorResponse) => handleError(error, req, router, toastr))
  );
}

const handleError = (
  error: HttpErrorResponse,
  req: HttpRequest<any>,
  router: Router,
  toastr: ToastrService
): Observable<never> => {
  if (req.url.includes('/synthese-period') && error.status === 404) {
    return throwError(() => error);
  }

  showError(error, router, toastr);
  return throwError(() => error);
}

const showError = (
  httpErrorResponse: HttpErrorResponse,
  router: Router,
  toastr: ToastrService
) => {
  if (httpErrorResponse.status === 404 || httpErrorResponse.status === 401) {
    router.navigate(['/not-found'], {skipLocationChange: true});
  }

  const errorObj = httpErrorResponse.error;
  if (errorObj) {
    if (typeof errorObj.error_description === 'string') {
      toastr.error(errorObj.error_description, `Erreur ${httpErrorResponse.status}`);
    } else if (typeof errorObj.message === 'string') {
      toastr.error(errorObj.message, `Erreur ${httpErrorResponse.status}`);
    } else {
      toastr.error(errorObj);
    }
  } else {
    toastr.error(httpErrorResponse.message || 'Une erreur est survenue');
  }
}
